/**
 * PageTourOverlay — in-context spotlight tour for the CURRENT page (immersive
 * help, ProjectTasks "more interactive/immersive learning in the help section").
 *
 * Launched from the "Take a guided tour" button in the HowThisWorks help modal.
 * Reads the active tour from pageTourStore + the step list from the pageTours
 * registry, spotlights each step's target element, and shows a caption with
 * Back / Next / Done. When a step's target is not on the page, that step falls
 * back to a centered card (never breaks). No routing, no persistence: this is a
 * single-page walk, distinct from the #109 sandbox walkthrough.
 */

import { useEffect, useLayoutEffect, useState, useCallback } from 'react'
import { ChevronLeft, ChevronRight, X, Check, Compass } from 'lucide-react'
import { usePageTourStore } from './pageTourStore'
import { getPageTour } from './pageTours'
import { cn } from '@/lib/utils'

interface Rect { top: number; left: number; width: number; height: number }
const PAD = 8

export function PageTourOverlay() {
  const activeTourKey = usePageTourStore((s) => s.activeTourKey)
  const mode = usePageTourStore((s) => s.mode)
  const stepIndex = usePageTourStore((s) => s.stepIndex)
  const next = usePageTourStore((s) => s.next)
  const prev = usePageTourStore((s) => s.prev)
  const exit = usePageTourStore((s) => s.exit)
  // Hop back to free-explore (all sections at once) without losing the overlay.
  const startExplore = usePageTourStore((s) => s.startExplore)

  const [rect, setRect] = useState<Rect | null>(null)

  const tour = activeTourKey ? getPageTour(activeTourKey) : null
  const step = tour ? tour[stepIndex] : null
  const total = tour?.length ?? 0
  const isLast = stepIndex >= total - 1

  // Scroll the target into view once per step.
  useEffect(() => {
    if (!step?.selector) return
    const sel = step.selector
    const t = setTimeout(() => {
      const el = document.querySelector(sel)
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 60)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTourKey, stepIndex])

  const measure = useCallback(() => {
    if (!step?.selector) { setRect(null); return }
    const el = document.querySelector(step.selector)
    if (!el) { setRect(null); return }
    const r = el.getBoundingClientRect()
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
  }, [step?.selector])

  useLayoutEffect(() => {
    if (!activeTourKey) return
    const raf = requestAnimationFrame(measure)
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [activeTourKey, stepIndex, measure])

  // Esc exits.
  useEffect(() => {
    if (!activeTourKey) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') exit() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeTourKey, exit])

  if (!activeTourKey || mode !== 'tour' || !tour || !step) return null

  const spot = rect && {
    top: rect.top - PAD, left: rect.left - PAD,
    width: rect.width + PAD * 2, height: rect.height + PAD * 2,
  }

  // Caption position: under the target if there's room, else above; centered
  // when there is no target.
  let cardStyle: React.CSSProperties = {}
  let centered = true
  if (spot) {
    centered = false
    const below = spot.top + spot.height + 12
    const wouldOverflow = below + 200 > window.innerHeight
    cardStyle = wouldOverflow
      ? { top: Math.max(8, spot.top - 12 - 200), left: Math.min(spot.left, window.innerWidth - 380) }
      : { top: below, left: Math.min(spot.left, window.innerWidth - 380) }
  }

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Guided page tour">
      {spot ? (
        <div
          className="absolute rounded-lg ring-2 ring-brand-400 transition-all duration-200 pointer-events-none"
          style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height, boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)' }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-900/55" />
      )}

      <div
        className={cn(
          'absolute w-[360px] max-w-[calc(100vw-32px)] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden',
          centered && 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
        )}
        style={centered ? undefined : cardStyle}
      >
        <div className="px-4 py-3 bg-gradient-to-r from-brand-600 to-indigo-600 text-white">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-white/80">
              <Compass className="w-3.5 h-3.5" /> Guided tour
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => startExplore(activeTourKey)}
                title="Switch to Explore — every section at once"
                className="px-1.5 py-1 rounded hover:bg-white/20 transition-colors text-[10px] font-semibold uppercase tracking-wide"
              >
                Explore
              </button>
              <button onClick={exit} aria-label="Exit tour" className="p-1 rounded hover:bg-white/20 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <h3 className="text-sm font-semibold mt-1">{step.title}</h3>
        </div>

        <div className="px-4 py-3 space-y-2">
          <p className="text-sm font-medium text-slate-800 leading-snug">{step.body}</p>
          {step.points && step.points.length > 0 && (
            <ul className="space-y-1">
              {step.points.map((pt, k) => (
                <li key={k} className="flex items-start gap-2 text-[13px] text-slate-600 leading-snug">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-brand-500 shrink-0" />
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-4 pb-2 flex items-center gap-1">
          {tour.map((s, i) => (
            <div key={s.title + i} className={cn('h-1.5 rounded-full transition-all', i === stepIndex ? 'w-5 bg-brand-500' : i < stepIndex ? 'w-1.5 bg-brand-300' : 'w-1.5 bg-slate-200')} />
          ))}
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
          <button
            onClick={prev}
            disabled={stepIndex === 0}
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-600 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="flex items-center gap-2 text-[11px] text-slate-400">
            <span>{stepIndex + 1} / {total}</span>
            <button
              onClick={isLast ? exit : next}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
            >
              {isLast ? <Check className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
